import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  IconPlus,
  IconDatabaseOff,
  IconRefresh,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import {
  isTaskListReference,
  type TaskListDefinition,
  type TemplateTaskConfig,
  type TemplateTaskConfigCatalog,
} from '@techgitter/contracts';
import type { TicketTypeTemplate } from '../../../types/ticketTypeTemplate';
import { CreateTemplateTaskConfigDialog } from './CreateTemplateTaskConfigDialog';
import { TemplateTaskConfigDetailEditor } from './TemplateTaskConfigDetailEditor';
import { HelpHint } from '../HelpHint';
import { EditorModeToggle } from '../EditorModeToggle';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton, ToolbarSearch } from '../../toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../../table';
import { Chip } from '../../chip';
import type { EditorMode } from '../../../hooks/useJsonGuiToggle';

interface TemplateTaskConfigGuiEditorProps {
  mode: EditorMode;
  onModeChange: (mode: EditorMode) => void;
  catalog: TemplateTaskConfigCatalog;
  ticketTemplates: TicketTypeTemplate[];
  taskLists: TaskListDefinition[];
  onCatalogChange: (catalog: TemplateTaskConfigCatalog) => void;
  onSave: () => void;
  onClearCache: () => void;
  onReload: () => void;
  saving: boolean;
  clearing: boolean;
}

export const TemplateTaskConfigGuiEditor: React.FC<TemplateTaskConfigGuiEditorProps> = ({
  mode,
  onModeChange,
  catalog,
  ticketTemplates,
  taskLists,
  onCatalogChange,
  onSave,
  onClearCache,
  onReload,
  saving,
  clearing,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TemplateTaskConfig | null>(null);
  const [filter, setFilter] = useState('');

  const configs = useMemo(() => catalog.configs ?? [], [catalog.configs]);
  const editing = editingId ? configs.find((c) => c.ticketTypeId === editingId) ?? null : null;

  const ticketTemplateMap = useMemo(
    () => new Map(ticketTemplates.map((t) => [t.ticketTypeId, t])),
    [ticketTemplates],
  );
  const taskListIds = useMemo(() => new Set(taskLists.map((tl) => tl.taskListId)), [taskLists]);

  const filteredConfigs = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return configs;
    return configs.filter((c) =>
      [c.ticketTypeId, ticketTemplateMap.get(c.ticketTypeId)?.displayName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }, [configs, filter, ticketTemplateMap]);

  const handleCreate = (config: TemplateTaskConfig) => {
    onCatalogChange({ ...catalog, configs: [...configs, config] });
    setCreateOpen(false);
    setEditingId(config.ticketTypeId);
  };

  const handleConfigChange = (updated: TemplateTaskConfig) => {
    onCatalogChange({
      ...catalog,
      configs: configs.map((c) =>
        c.ticketTypeId === updated.ticketTypeId ? updated : c,
      ),
    });
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    onCatalogChange({
      ...catalog,
      configs: configs.filter((c) => c.ticketTypeId !== deleteTarget.ticketTypeId),
    });
    setDeleteTarget(null);
  };

  const refIssues = (config: TemplateTaskConfig): number => {
    return (config.sections ?? []).reduce((sum, section) => {
      if (isTaskListReference(section) && !taskListIds.has(section.taskListId)) {
        return sum + 1;
      }
      return sum;
    }, 0);
  };

  // Detail view
  if (editing) {
    return (
      <TemplateTaskConfigDetailEditor
        config={editing}
        ticketTemplate={ticketTemplateMap.get(editing.ticketTypeId)}
        taskLists={taskLists}
        onChange={handleConfigChange}
        onBack={() => setEditingId(null)}
        onSave={onSave}
        saving={saving}
      />
    );
  }

  // List view
  return (
    <>
      <SecondaryToolbar floating>
        <EditorModeToggle mode={mode} onChange={onModeChange} />
        <ToolbarSearch
          searchValue={filter}
          onSearchChange={setFilter}
          searchPlaceholder="Filter by ticket type ID or template…"
        />
        <ToolbarSpacer />
        <ToolbarButton
          variant="primary"
          icon={IconPlus}
          onClick={() => setCreateOpen(true)}
          title="Bind a task checklist to a ticket type that doesn't have one yet. Pick from existing ticket types you haven't yet configured."
        >
          Create Config
        </ToolbarButton>
        <ToolbarButton
          onClick={onSave}
          title="Apply all current edits. Diffs against the live state and only writes what's changed."
        >
          {saving ? 'Saving…' : 'Save'}
        </ToolbarButton>
        <ToolbarButton
          icon={IconDatabaseOff}
          onClick={onClearCache}
          title="Bypass any server-side cache on the next read."
        >
          {clearing ? 'Clearing…' : 'Clear Cache'}
        </ToolbarButton>
        <ToolbarButton
          icon={IconRefresh}
          onClick={onReload}
          title="Fetch fresh data from the server. Discards unsaved edits."
        >
          Reload
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell>
                  Ticket Type ID
                  <HelpHint text="The slug identifying which ticket type this config applies to. Must match an existing ticket-type-template's ticketTypeId." />
                </DataTableHeadCell>
                <DataTableHeadCell>
                  Linked Template
                  <HelpHint text="The display name of the matching ticket-type-template. A warning appears if no template with this ID exists — usually means the ticket type was renamed or deleted." />
                </DataTableHeadCell>
                <DataTableHeadCell align="center">
                  Sections
                  <HelpHint text="Number of sections in this config. A section is either defined inline here or references a reusable Task List." />
                </DataTableHeadCell>
                <DataTableHeadCell align="center">
                  Issues
                  <HelpHint text="Number of references pointing at unknown task list IDs. Fix by editing the config or creating the missing task list on the Task Lists page." />
                </DataTableHeadCell>
                <DataTableHeadCell align="right">Actions</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {filteredConfigs.length === 0 ? (
                <DataTableEmpty colSpan={5}>
                  {filter.trim()
                    ? `No configs match "${filter.trim()}".`
                    : 'No task template configs yet. Click "Create Config" to add one.'}
                </DataTableEmpty>
              ) : (
                filteredConfigs.map((c) => {
                  const tmpl = ticketTemplateMap.get(c.ticketTypeId);
                  const issues = refIssues(c);
                  return (
                    <DataTableRow key={c.ticketTypeId}>
                      <DataTableCell style={{ fontFamily: 'monospace' }}>
                        {c.ticketTypeId}
                      </DataTableCell>
                      <DataTableCell>
                        {tmpl ? (
                          tmpl.displayName
                        ) : (
                          <Chip tone="warning" title="No matching ticket type template found">
                            Unknown ticket type
                          </Chip>
                        )}
                      </DataTableCell>
                      <DataTableCell align="center">{(c.sections ?? []).length}</DataTableCell>
                      <DataTableCell align="center">
                        {issues > 0 ? (
                          <Chip
                            tone="warning"
                            title={`${issues} reference(s) point to unknown task list ID(s)`}
                          >
                            {issues}
                          </Chip>
                        ) : (
                          '—'
                        )}
                      </DataTableCell>
                      <DataTableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setEditingId(c.ticketTypeId)} sx={{ color: '#888' }}>
                            <IconPencil size={16} stroke={1.5} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteTarget(c)} sx={{ color: '#888' }}>
                            <IconTrash size={16} stroke={1.5} />
                          </IconButton>
                        </Tooltip>
                      </DataTableCell>
                    </DataTableRow>
                  );
                })
              )}
            </tbody>
          </DataTable>
        </DataTableCard>
      </Box>

      <CreateTemplateTaskConfigDialog
        open={createOpen}
        ticketTemplates={ticketTemplates}
        existingTicketTypeIds={configs.map((c) => c.ticketTypeId)}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />

      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Config</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete the task template config for "{deleteTarget?.ticketTypeId}"? This cannot be
            undone after saving.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: '#888' }}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disableElevation>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
